import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Send, Plus, Loader as Loader2, Bot, User, ThumbsUp, Pencil, X, Check, Clock, MessageCircle, Trash2 } from 'lucide-react';

interface Session {
  id: string;
  title: string;
  messages_count: number;
  corrections_count: number;
  created_at: string;
}

interface Message {
  id: string;
  sender: 'manager' | 'agent';
  content: string;
  correction: string | null;
  correction_at: string | null;
  created_at: string;
}

export default function TrainingTab({ sellerId }: { sellerId: string }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [correctionText, setCorrectionText] = useState('');
  const [savingCorrection, setSavingCorrection] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSessions();
  }, [sellerId]);

  useEffect(() => {
    if (activeSessionId) loadMessages(activeSessionId);
  }, [activeSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadSessions() {
    setLoading(true);
    const { data } = await supabase
      .from('ai_training_sessions')
      .select('id, title, messages_count, corrections_count, created_at')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(20);
    setSessions((data ?? []) as Session[]);
    setLoading(false);
  }

  async function loadMessages(sessionId: string) {
    const { data } = await supabase
      .from('ai_training_messages')
      .select('id, sender, content, correction, correction_at, created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    setMessages((data ?? []) as Message[]);
  }

  async function createSession() {
    if (!user) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    const resp = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-train`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ action: 'create_session', seller_id: sellerId }),
      },
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      alert(json?.error || 'Erro ao criar sessao');
      return;
    }
    const newSession = json.session as Session;
    setSessions((prev) => [newSession, ...prev]);
    setActiveSessionId(newSession.id);
    setMessages([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  async function sendMessage() {
    if (!input.trim() || !activeSessionId || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    const tempManagerMsg: Message = {
      id: 'temp-' + Date.now(),
      sender: 'manager',
      content: text,
      correction: null,
      correction_at: null,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempManagerMsg]);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-train`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'send_message', session_id: activeSessionId, content: text }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Erro ao enviar');
      const agentMsg = json.reply as Message;
      setMessages((prev) => [...prev, agentMsg]);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeSessionId ? { ...s, messages_count: s.messages_count + 2 } : s,
        ),
      );
    } catch (err) {
      const errorMsg: Message = {
        id: 'err-' + Date.now(),
        sender: 'agent',
        content: `[Erro: ${(err as Error).message}]`,
        correction: null,
        correction_at: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  async function submitCorrection(messageId: string) {
    if (!correctionText.trim()) return;
    setSavingCorrection(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-train`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'correct', message_id: messageId, correction: correctionText.trim() }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || 'Erro ao salvar');
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, correction: correctionText.trim(), correction_at: new Date().toISOString() } : m,
        ),
      );
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeSessionId ? { ...s, corrections_count: s.corrections_count + 1 } : s,
        ),
      );
      setCorrecting(null);
      setCorrectionText('');
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSavingCorrection(false);
    }
  }

  async function deleteSession(sessionId: string) {
    if (!confirm('Apagar esta sessao de treino?')) return;
    await supabase.from('ai_training_sessions').delete().eq('id', sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
      setMessages([]);
    }
  }

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  return (
    <div className="flex h-[600px] gap-4">
      {/* Sessions sidebar */}
      <div className="flex w-64 flex-shrink-0 flex-col rounded-xl border border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-3">
          <h3 className="text-sm font-semibold text-slate-700">Sessoes</h3>
          <button
            onClick={createSession}
            className="rounded-lg bg-emerald-600 p-1.5 text-white transition hover:bg-emerald-700"
            title="Nova sessao"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <div className="px-2 py-8 text-center text-xs text-slate-400">
              Nenhuma sessao ainda. Clique em + para comecar.
            </div>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={
                'group flex cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 transition ' +
                (activeSessionId === s.id
                  ? 'bg-emerald-100 border border-emerald-300'
                  : 'hover:bg-white border border-transparent')
              }
              onClick={() => setActiveSessionId(s.id)}
            >
              <MessageCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-xs font-medium text-slate-800">{s.title}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                  <span>{s.messages_count} msgs</span>
                  {s.corrections_count > 0 && (
                    <span className="text-amber-600">{s.corrections_count} correcoes</span>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                className="rounded p-0.5 opacity-0 transition group-hover:opacity-100 hover:bg-red-100"
              >
                <Trash2 className="h-3 w-3 text-red-400" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex flex-1 flex-col rounded-xl border border-slate-200 bg-white">
        {!activeSessionId ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center p-8">
            <div className="rounded-2xl bg-emerald-50 p-4">
              <Bot className="h-10 w-10 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Treinar o Agente</h3>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                Crie uma sessao para conversar com o agente como se voce fosse um lead.
                Teste objecoes, perguntas e cenarios. Corrija respostas inadequadas para ensina-lo.
              </p>
            </div>
            <button
              onClick={createSession}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" /> Nova sessao de treino
            </button>
          </div>
        ) : (
          <>
            {/* Messages list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                  <Bot className="h-8 w-8 text-slate-300" />
                  <p className="text-sm text-slate-400">
                    Envie uma mensagem como se fosse um lead interessado em emprestimo.
                  </p>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={
                    'flex ' + (msg.sender === 'manager' ? 'justify-end' : 'justify-start')
                  }
                >
                  <div
                    className={
                      'group relative max-w-[75%] rounded-2xl px-4 py-2.5 ' +
                      (msg.sender === 'manager'
                        ? 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-800 rounded-bl-md')
                    }
                  >
                    <div className="flex items-start gap-2">
                      {msg.sender === 'agent' && <Bot className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />}
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</div>
                      {msg.sender === 'manager' && <User className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-200" />}
                    </div>
                    <div
                      className={
                        'mt-1 flex items-center gap-1 text-[10px] ' +
                        (msg.sender === 'manager' ? 'text-emerald-200 justify-end' : 'text-slate-400')
                      }
                    >
                      <Clock className="h-2.5 w-2.5" />
                      {formatTime(msg.created_at)}
                    </div>

                    {/* Correction feedback for agent messages */}
                    {msg.sender === 'agent' && !msg.id.startsWith('err-') && (
                      <div className="mt-2 border-t border-slate-200 pt-2">
                        {msg.correction ? (
                          <div className="rounded-lg bg-amber-50 p-2">
                            <div className="flex items-center gap-1 text-[10px] font-medium text-amber-700 mb-1">
                              <Pencil className="h-3 w-3" /> Correcao aplicada
                            </div>
                            <p className="text-xs text-amber-900">{msg.correction}</p>
                          </div>
                        ) : correcting === msg.id ? (
                          <div className="space-y-2">
                            <input
                              value={correctionText}
                              onChange={(e) => setCorrectionText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  submitCorrection(msg.id);
                                }
                              }}
                              placeholder="Como deveria ter respondido?"
                              className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-200"
                              autoFocus
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={() => submitCorrection(msg.id)}
                                disabled={savingCorrection || !correctionText.trim()}
                                className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2 py-1 text-[10px] font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                              >
                                {savingCorrection ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Salvar
                              </button>
                              <button
                                onClick={() => { setCorrecting(null); setCorrectionText(''); }}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50"
                              >
                                <X className="h-3 w-3" /> Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-slate-500 hover:bg-emerald-50 hover:text-emerald-700"
                              title="Boa resposta"
                            >
                              <ThumbsUp className="h-3 w-3" /> Boa
                            </button>
                            <button
                              onClick={() => { setCorrecting(msg.id); setCorrectionText(''); }}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-slate-500 hover:bg-amber-50 hover:text-amber-700"
                              title="Corrigir resposta"
                            >
                              <Pencil className="h-3 w-3" /> Corrigir
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 rounded-bl-md">
                    <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                    <span className="text-xs text-slate-500">Agente pensando...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Escreva como se fosse um lead..."
                  disabled={sending}
                  className="flex-1 rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                />
                <button
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  className="rounded-xl bg-emerald-600 p-2.5 text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-slate-400">
                Nenhuma mensagem e enviada pelo WhatsApp. Isso e apenas uma simulacao para testar o agente.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
